import { useCallback } from 'react'
import { simpleEditNotification } from 'hooks/notificationHandler'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { useRecipientsSelectContext } from 'components/CIMSSelect/common/RecipientsSelect/RecipientsSelectContext'
import { useDSNotificationsContext } from '../../DSNotificationsContext'
import { EditNotificationEventOptionsFn } from 'components/CIMSNotifications/types'
import { Permission } from '@cims-online/shared'
import { LabeledValueType } from 'components/CIMSSelect/types'
import RenderFormItems from './components/RenderFormItems'
import { DisciplineFormValue } from './types'
import {
  UpsertDsNotificationInput,
  useUpsertDsNotificationMutation,
} from 'schema'
import { useProjectContext } from 'context/ProjectContext'

export function useEditFn() {
  const { projectId } = useProjectContext()
  const { recipients, refetch } = useDSNotificationsContext()
  const { toLabeledValues, fromLabeledValues } = useRecipientsSelectContext()
  const [edit] = useUpsertDsNotificationMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      upsertDSNotification: {
        notification: { notificationEvent },
      },
    }) => simpleEditNotification(notificationEvent),
  })
  return useCallback<EditNotificationEventOptionsFn<InitialValues>>(
    (event) => {
      const { every, byDSDiscipline } = recipients(event)
      return {
        render: <RenderFormItems />,
        initialValues: {
          recipients: every ? toLabeledValues(every) : [],
          dsDiscipline: byDSDiscipline?.map(({ discipline, recipients }) => ({
            dsDisciplineId: discipline.id,
            recipients: toLabeledValues(recipients),
          })),
        },
        permission: Permission.EditDSNotification,
        onSave: async ({ recipients, dsDiscipline }) => {
          const input: UpsertDsNotificationInput = {
            notificationEventId: event.id,
            projectId,
            every: fromLabeledValues(recipients),
            byDSDiscipline: dsDiscipline?.map(({ recipients, ...rest }) => ({
              ...rest,
              recipients: fromLabeledValues(recipients),
            })),
          }

          await edit({ variables: { input } })
          refetch()
        },
        onClear: async () => {
          await edit({
            variables: {
              input: {
                notificationEventId: event.id,
                projectId,
                every: { userIds: [], nonUserEmailIds: [] },
                byDSDiscipline: [],
              },
            },
          })
        },
      }
    },
    [edit, recipients, projectId, refetch, toLabeledValues, fromLabeledValues]
  )
}

interface InitialValues {
  recipients: LabeledValueType[]
  dsDiscipline?: DisciplineFormValue[]
}
